import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { PublicUserDto } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { FollowButton } from '../components/FollowButton';
import { PlaylistCard } from '../components/cards';
import { IconLibrary, IconSettings, IconUser, IconUsers } from '../components/icons';
import { Avatar, CardSkeletons, EmptyState, ErrorState, Loading, Segmented } from '../components/ui';
import { useAsync } from '../hooks/useAsync';
import { t } from '../i18n';
import { formatDate, itemsWord } from '../lib/format';

type Tab = 'playlists' | 'followers' | 'following';

function UserRow({ person }: { person: PublicUserDto }) {
  return (
    <div className="user-row">
      <Avatar name={person.displayName} src={person.avatarUrl} />
      <div className="user-row__main">
        <Link to={`/u/${person.username}`} className="strong clamp-1">
          {person.displayName}
        </Link>
        <p className="tiny muted clamp-1">
          @{person.username}
          {person.bio ? ` · ${person.bio}` : ''}
        </p>
      </div>
      <Link className="btn btn--ghost btn--sm" to={`/u/${person.username}`}>
        {t('nav.profile')}
      </Link>
    </div>
  );
}

function FollowList({ username, tab }: { username: string; tab: 'followers' | 'following' }) {
  const state = useAsync(
    () => (tab === 'followers' ? api.users.followers(username) : api.users.following(username)),
    [username, tab],
  );

  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.loading || !state.data) return <Loading rows={3} />;
  if (state.data.users.length === 0) {
    return (
      <EmptyState
        icon={<IconUsers />}
        title={tab === 'followers' ? t('profile.noFollowersTitle') : t('profile.noFollowingTitle')}
        body={tab === 'followers' ? t('profile.noFollowersBody') : t('profile.noFollowingBody')}
        action={
          tab === 'following' ? (
            <Link className="btn btn--outline" to="/app/search">
              {t('feed.findPeople')}
            </Link>
          ) : undefined
        }
      />
    );
  }
  return (
    <div className="panel stack-sm">
      {state.data.users.map((person) => (
        <UserRow key={person.id} person={person} />
      ))}
    </div>
  );
}

function StatTile({
  value,
  label,
  active,
  onSelect,
}: {
  value: number;
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button type="button" className={`stat${active ? ' stat--active' : ''}`} onClick={onSelect} aria-pressed={active}>
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
    </button>
  );
}

export function UserView({ username }: { username: string }) {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('playlists');
  const profileState = useAsync(() => api.users.profile(username), [username]);
  const playlistsState = useAsync(() => api.users.playlists(username), [username]);

  if (profileState.error) {
    const missing = profileState.error instanceof ApiError && profileState.error.status === 404;
    return (
      <div className="page page--read">
        {missing ? (
          <EmptyState
            icon={<IconUser />}
            title={t('profile.notFoundTitle')}
            body={t('profile.notFoundBody', { username })}
            action={
              <Link className="btn btn--primary" to={user ? '/app' : '/'}>
                {user ? t('notfound.feed') : t('notfound.home')}
              </Link>
            }
          />
        ) : (
          <ErrorState error={profileState.error} onRetry={profileState.reload} />
        )}
      </div>
    );
  }

  if (profileState.loading || !profileState.data) {
    return (
      <div className="page">
        <Loading rows={4} />
      </div>
    );
  }

  const profile = profileState.data;
  const isSelf = profile.viewerRelation.isSelf;

  return (
    <div className="page">
      <header className="profile-head">
        <div className="profile-head__row">
          <Avatar name={profile.displayName} src={profile.avatarUrl} size="lg" />
          <div className="profile-head__identity">
            <h1 className="profile-head__name">{profile.displayName}</h1>
            <p className="profile-head__handle">
              @{profile.username} · {t('profile.since')} {formatDate(profile.createdAt)}
            </p>
          </div>
          {isSelf ? (
            <Link className="btn btn--outline btn--sm" to="/app/settings">
              <IconSettings size={17} /> {t('nav.settings')}
            </Link>
          ) : (
            <FollowButton
              userId={profile.id}
              following={profile.viewerRelation.isFollowing}
              onToggle={profileState.reload}
            />
          )}
        </div>

        {profile.bio ? <p className="dim">{profile.bio}</p> : null}

        <div className="stats">
          <StatTile
            value={profile.counts.playlists}
            label={t('profile.statCollections', { n: profile.counts.playlists })}
            active={tab === 'playlists'}
            onSelect={() => setTab('playlists')}
          />
          <StatTile
            value={profile.counts.followers}
            label={t('profile.statFollowers', { n: profile.counts.followers })}
            active={tab === 'followers'}
            onSelect={() => setTab('followers')}
          />
          <StatTile
            value={profile.counts.following}
            label={t('profile.statFollowing', { n: profile.counts.following })}
            active={tab === 'following'}
            onSelect={() => setTab('following')}
          />
          {isSelf ? (
            <Link className="stat" to="/app/library">
              <span className="stat__value">{profile.counts.items}</span>
              <span className="stat__label">{t('profile.statLibrary', { n: profile.counts.items })}</span>
            </Link>
          ) : null}
        </div>

        {isSelf ? (
          <p className="tiny muted">
            {t('profile.libraryPrivacy')}
            {profile.counts.items > 0 ? t('profile.nowThere', { words: itemsWord(profile.counts.items) }) : ''}
          </p>
        ) : null}
      </header>

      <div className="toolbar" style={{ marginTop: 'var(--sp-5)' }}>
        <Segmented
          name="profile-tab"
          label={t('profile.tabLabel')}
          value={tab}
          onChange={setTab}
          options={[
            { value: 'playlists', label: t('nav.playlists') },
            { value: 'followers', label: t('profile.tabFollowers', { n: profile.counts.followers }) },
            { value: 'following', label: t('profile.tabFollowing', { n: profile.counts.following }) },
          ]}
        />
      </div>

      {tab === 'playlists' ? (
        playlistsState.error ? (
          <ErrorState error={playlistsState.error} onRetry={playlistsState.reload} />
        ) : playlistsState.loading ? (
          <CardSkeletons count={3} variant="playlist" />
        ) : playlistsState.data && playlistsState.data.length > 0 ? (
          <div className="playlist-grid">
            {playlistsState.data.map((playlist) => (
              <PlaylistCard
                key={playlist.id}
                playlist={playlist}
                to={isSelf ? `/app/playlists/${playlist.id}` : `/p/${playlist.id}`}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<IconLibrary />}
            title={isSelf ? t('profile.noPublicMine') : t('profile.noPublicTheirs')}
            body={isSelf ? t('profile.noPublicMineBody') : t('profile.noPublicTheirsBody')}
            action={
              isSelf ? (
                <Link className="btn btn--primary" to="/app/playlists/new">
                  {t('feed.createPlaylist')}
                </Link>
              ) : undefined
            }
          />
        )
      ) : (
        <FollowList username={profile.username} tab={tab} />
      )}
    </div>
  );
}

/** Public profile at /u/:username — reachable without an account. */
export function UserPage() {
  const { username = '' } = useParams();
  return <UserView username={username.toLowerCase()} />;
}

/** The same screen inside the app shell, always pointed at the signed-in user. */
export function OwnProfilePage() {
  const { user } = useAuth();
  if (!user) return null;
  return <UserView username={user.username} />;
}
